import axios from 'axios';

/**
 * Routes that render outside `ProtectedRoute` in App.tsx. A 401 on these
 * pages is expected (no session yet) and must never bounce to /login.
 * Keep in step with the public `<Route>`s at the top of App.tsx.
 */
const PUBLIC_PATH_PREFIXES = [
  '/login',
  '/forgot-password',
  '/reset-password',
  '/logout',
  '/portal/client',
  '/portal/vendor',
  '/payment-confirmation',
  '/warranty/register',
  '/jobopenings',
  '/careers',
];

export function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export const api = axios.create({
  baseURL: '/api/v1',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.response.use(
  (res) => {
    // Apr 23, 2026 (Bug 7) — auto-refresh credit chip whenever any AI feature
    // or an explicit /credits/deduct call succeeds. Keeps TopBar in sync
    // without every call site having to remember to bump the bus.
    try {
      const method = (res.config?.method ?? '').toLowerCase();
      const url = res.config?.url ?? '';
      const isMutating = method === 'post' || method === 'put' || method === 'patch';
      if (isMutating && (url.startsWith('/ai/') || url.includes('/credits/deduct'))) {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('aura:credits:refresh'));
        }
      }
    } catch {
      // Ignore — this is best-effort UX sugar, not critical.
    }
    return res;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Redirect to login unless the page is public. The auth store fires
      // `GET /auth/me` at module load, which 401s for every signed-out
      // visitor — including one who just opened the password-reset email.
      if (!isPublicPath(window.location.pathname)) {
        window.location.href = '/login';
      }
    }

    // 402 — Kiaan subscription required. Dispatch a custom event so the AIPanel
    // can render the KiaanLockedCard without every call site needing to handle it.
    if (
      error.response?.status === 402 &&
      error.response?.data?.error === 'kiaan_subscription_required'
    ) {
      try {
        const payload = error.response.data;
        window.dispatchEvent(
          new CustomEvent('aura:kiaan:locked', {
            detail: {
              error: payload.error,
              message: payload.message ?? 'Subscribe to Kiaan to unlock AI features.',
              ctaUrl: payload.ctaUrl ?? 'https://kiaan.ai',
              ctaLabel: payload.ctaLabel ?? 'Explore Kiaan',
            },
          })
        );
      } catch {
        // Best-effort — don't break the error chain.
      }
    }

    return Promise.reject(error);
  }
);

// ── Manufacturing / Production planning helpers ─────────────────────────────
// Thin wrappers so pages don't hard-code endpoints. Safe to evolve as the
// backend adds bucketed responses — we fall back to client-side splitting
// when the API still returns a flat list.

export type PlanningBucket = 'today' | 'tomorrow' | 'thisWeek' | 'backlog';

export async function getProductionPlan(params?: { bucket?: PlanningBucket }) {
  const res = await api.get('/manufacturing-flow/planning', {
    params: params?.bucket ? { bucket: params.bucket } : undefined,
  });
  return res.data?.data ?? res.data;
}

export async function startShift(payload: { orderIds?: string[]; productionOrderIds?: string[] } = {}) {
  // Backend contract: POST /manufacturing-p0/shift/start { productionOrderIds: string[] }
  const productionOrderIds =
    payload.productionOrderIds ?? payload.orderIds ?? [];
  const res = await api.post('/manufacturing-p0/shift/start', { productionOrderIds });
  return res.data?.data ?? res.data;
}
